import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { z } from 'zod';

import {
  ScheduledBankTransferEndDateDeterminedBy,
  ScheduledBankTransferFrequency,
} from '__generated__/GQL';
import { nonEmptyStringSchema } from 'common/zod/schemas';

export type BankTransferCreationStep =
  | 'init'
  | 'selectFromAccount'
  | 'selectBeneficiary'
  | 'selectToAccount'
  | 'setInternalDetails'
  | 'setExternalDetails'
  | 'addReceipt'
  | 'confirmInternalDetails'
  | 'confirmExternalDetails'
  | 'performInternal'
  | 'performExternal'
  | 'externalTransferSuccess'
  | 'internalTransferSuccess'
  | 'failure';

const bankTransferInputScheduleSchema = z.object({
  endDateDeterminedBy: z
    .nativeEnum(ScheduledBankTransferEndDateDeterminedBy)
    .optional(),
  frequency: z.nativeEnum(ScheduledBankTransferFrequency),
  lastDueDate: z.string().optional(),
  nextDueDate: z.string(),
  remainingNumberOfOccurrences: z.number().optional(),
});

export type BankTransferInputSchedule = z.infer<
  typeof bankTransferInputScheduleSchema
>;

const bankTransferReceiptSchema = z.object({
  bucket: nonEmptyStringSchema,
  fileRef: nonEmptyStringSchema,
  url: nonEmptyStringSchema,
});

const internationalTransferCurrencySchema = z.object({
  code: nonEmptyStringSchema,
  countryCode: nonEmptyStringSchema,
  label: nonEmptyStringSchema,
});

export type InternationalTransferCurrency = z.infer<
  typeof internationalTransferCurrencySchema
>;

export type BankTransferReceipt = z.infer<typeof bankTransferReceiptSchema>;

export const externalTransferInputSchema = z.object({
  amount: z.number().positive(),
  bankTransferRecipientId: nonEmptyStringSchema,
  fromBankAccountId: nonEmptyStringSchema,
  isInstant: z.boolean(),
  label: z.string().optional(),
  receipt: bankTransferReceiptSchema.optional(),
  recipientEmail: z.string().optional(),
  schedule: bankTransferInputScheduleSchema.optional(),
  shouldNotifyRecipient: z.boolean().optional(),
  transferType: z.literal('external'),
});

export const internalTransferInputSchema = z.object({
  amount: z.number().positive(),
  fromBankAccountId: nonEmptyStringSchema,
  label: z.string().optional(),
  toBankAccountId: nonEmptyStringSchema,
  transferType: z.literal('internal'),
});

export const internationalTransferInputSchema = z.object({
  fromBankAccountId: nonEmptyStringSchema,
  transferType: z.literal('international'),
});

const bankTransferInputSchema = z.union([
  externalTransferInputSchema,
  internalTransferInputSchema,
]);

export type BankTransferInput = z.infer<typeof bankTransferInputSchema>;

const partialBankTransferInputSchema = z.union([
  externalTransferInputSchema.partial().required({ transferType: true }),
  internalTransferInputSchema.partial().required({ transferType: true }),
  internationalTransferInputSchema.partial().required({ transferType: true }),
]);

export type PartialBankTransferInput = z.infer<
  typeof partialBankTransferInputSchema
>;

const bankTransferInputAtSelectFromAccountStepSchema = z.union([
  externalTransferInputSchema.pick({ transferType: true }),
  internalTransferInputSchema
    .pick({ toBankAccountId: true, transferType: true })
    .partial({ toBankAccountId: true }),
  internationalTransferInputSchema.pick({ transferType: true }),
]);

export type BankTransferInputAtSelectFromAccountStep = z.infer<
  typeof bankTransferInputAtSelectFromAccountStepSchema
>;

const bankTransferInputAtSelectBeneficiarySchema =
  externalTransferInputSchema.pick({
    fromBankAccountId: true,
    transferType: true,
  });

export type BankTransferInputAtSelectBeneficiaryStep = z.infer<
  typeof bankTransferInputAtSelectBeneficiarySchema
>;

const bankTransferInputAtSelectToAccountStepSchema = internalTransferInputSchema
  .pick({
    amount: true,
    fromBankAccountId: true,
    transferType: true,
  })
  .partial({
    amount: true,
  });

export type BankTransferInputAtSelectToAccountStep = z.infer<
  typeof bankTransferInputAtSelectToAccountStepSchema
>;

const bankTransferInputAtSetInternalDetailsStepSchema =
  internalTransferInputSchema
    .pick({
      amount: true,
      fromBankAccountId: true,
      label: true,
      toBankAccountId: true,
      transferType: true,
    })
    .partial({
      amount: true,
      label: true,
    });

export type BankTransferInputAtInternalDetailsStep = z.infer<
  typeof bankTransferInputAtSetInternalDetailsStepSchema
>;

const bankTransferInputAtSetExternalDetailsStepSchema =
  externalTransferInputSchema
    .pick({
      amount: true,
      bankTransferRecipientId: true,
      fromBankAccountId: true,
      isInstant: true,
      label: true,
      schedule: true,
      toBankAccountId: true,
      transferType: true,
    })
    .partial({
      amount: true,
      isInstant: true,
      label: true,
    });

export type BankTransferInputAtSetExternalDetailsStep = z.infer<
  typeof bankTransferInputAtSetExternalDetailsStepSchema
>;

const bankTransferInputAtConfirmInternalDetailsStepSchema =
  internalTransferInputSchema.pick({
    amount: true,
    fromBankAccountId: true,
    label: true,
    toBankAccountId: true,
    transferType: true,
  });

const bankTransferInputAtConfirmExternalDetailsStepSchema =
  externalTransferInputSchema.pick({
    amount: true,
    bankTransferRecipientId: true,
    fromBankAccountId: true,
    isInstant: true,
    label: true,
    schedule: true,
    transferType: true,
  });

const bankTransferInputAtFailureStepSchema = z.union([
  bankTransferInputAtConfirmExternalDetailsStepSchema,
  bankTransferInputAtConfirmInternalDetailsStepSchema,
]);

export interface BankTransferContextType {
  bankTransfer: PartialBankTransferInput;
  setBankTransfer: Dispatch<SetStateAction<PartialBankTransferInput>>;
}

export const BankTransferContext = createContext<BankTransferContextType>({
  bankTransfer: {
    transferType: 'external',
  },
  setBankTransfer: () => undefined,
});

type UseBankTransferReturnType<TBankTransferInput> = Omit<
  BankTransferContextType,
  'bankTransfer'
> & {
  bankTransfer: TBankTransferInput;
};

const unhandledBankTransferCreationStep = (step: never): never => {
  throw new Error(`Unhandled bank transfer creation step: ${step}`);
};

export function useBankTransferContext(): UseBankTransferReturnType<PartialBankTransferInput>;
export function useBankTransferContext(
  step: 'init',
): UseBankTransferReturnType<PartialBankTransferInput>;
export function useBankTransferContext(
  step: 'selectFromAccount',
): UseBankTransferReturnType<BankTransferInputAtSelectFromAccountStep>;
export function useBankTransferContext(
  step: 'selectBeneficiary',
): UseBankTransferReturnType<BankTransferInputAtSelectBeneficiaryStep>;
export function useBankTransferContext(
  step: 'selectToAccount',
): UseBankTransferReturnType<BankTransferInputAtSelectToAccountStep>;
export function useBankTransferContext(
  step: 'setExternalDetails',
): UseBankTransferReturnType<
  z.infer<typeof bankTransferInputAtSetExternalDetailsStepSchema>
>;
export function useBankTransferContext(
  step: 'setInternalDetails',
): UseBankTransferReturnType<
  z.infer<typeof bankTransferInputAtSetInternalDetailsStepSchema>
>;
export function useBankTransferContext(
  step: 'failure',
): UseBankTransferReturnType<
  z.infer<typeof bankTransferInputAtFailureStepSchema>
>;
export function useBankTransferContext(
  step: 'confirmInternalDetails',
): UseBankTransferReturnType<
  z.infer<typeof bankTransferInputAtConfirmInternalDetailsStepSchema>
>;
export function useBankTransferContext(
  step: 'confirmExternalDetails',
): UseBankTransferReturnType<
  z.infer<typeof bankTransferInputAtConfirmExternalDetailsStepSchema>
>;
export function useBankTransferContext(
  step: 'performExternal',
): UseBankTransferReturnType<z.infer<typeof externalTransferInputSchema>>;
export function useBankTransferContext(
  step: 'performInternal',
): UseBankTransferReturnType<z.infer<typeof internalTransferInputSchema>>;
export function useBankTransferContext(
  step: 'externalTransferSuccess',
): UseBankTransferReturnType<z.infer<typeof externalTransferInputSchema>>;
export function useBankTransferContext(
  step: 'internalTransferSuccess',
): UseBankTransferReturnType<z.infer<typeof internalTransferInputSchema>>;
export function useBankTransferContext(step?: BankTransferCreationStep) {
  const { bankTransfer, ...context } = useContext(BankTransferContext);

  try {
    switch (step) {
      case undefined:
      case 'init':
        return {
          ...context,
          bankTransfer: partialBankTransferInputSchema.parse(bankTransfer),
        };
      case 'selectFromAccount':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtSelectFromAccountStepSchema.parse(bankTransfer),
        };
      case 'selectBeneficiary':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtSelectBeneficiarySchema.parse(bankTransfer),
        };
      case 'selectToAccount':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtSelectToAccountStepSchema.parse(bankTransfer),
        };
      case 'setExternalDetails':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtSetExternalDetailsStepSchema.parse(bankTransfer),
        };
      case 'setInternalDetails':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtSetInternalDetailsStepSchema.parse(bankTransfer),
        };
      case 'confirmInternalDetails':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtConfirmInternalDetailsStepSchema.parse(
              bankTransfer,
            ),
        };
      case 'confirmExternalDetails':
      case 'addReceipt':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtConfirmExternalDetailsStepSchema.parse(
              bankTransfer,
            ),
        };
      case 'performInternal':
        return {
          ...context,
          bankTransfer: internalTransferInputSchema.parse(bankTransfer),
        };
      case 'performExternal':
        return {
          ...context,
          bankTransfer: externalTransferInputSchema.parse(bankTransfer),
        };
      case 'internalTransferSuccess':
        return {
          ...context,
          bankTransfer: internalTransferInputSchema.parse(bankTransfer),
        };
      case 'externalTransferSuccess':
        return {
          ...context,
          bankTransfer: externalTransferInputSchema.parse(bankTransfer),
        };
      case 'failure':
        return {
          ...context,
          bankTransfer:
            bankTransferInputAtFailureStepSchema.parse(bankTransfer),
        };
      default:
        return unhandledBankTransferCreationStep(step);
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(
        `Invalid bank transfer context state at step ${step}: ${error.message}`,
      );
    }
    throw error;
  }
}
